<script lang="ts">
  import type { NeXusResult, ValidationResult } from '../types';
  import { apiService } from '../services/api';
  import { notifications } from '../stores/notifications';

  export let result: NeXusResult;
  export let metadata: Record<string, any> = {};
  export let currentMapping: Record<string, string> = {};
  export let changedKeys: string[] = [];
  export let removedKeys: { key: string; oldPath: string }[] = [];

  $: originalMetadata = metadata;

  let activeTab = 'overview';

  // Validation state
  let validationResult: ValidationResult | null = null;
  let validating = false;

  // Create a set for efficient lookup
  $: changedKeysSet = new Set(changedKeys);

  // Set of paths that have validation errors, used to highlight in mapping tab
  $: validationErrorPaths = new Set(
    (validationResult?.errors ?? []).map(e => e.path).filter(p => p)
  );

  async function downloadNexusFile() {
    try {
      const blob = await apiService.downloadNexusFile(result.nexus_id);

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${result.nexus_id}.nxs`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      notifications.error(`Download failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async function validateNexus() {
    validating = true;
    try {
      validationResult = await apiService.validateNexusFile(result.nexus_id);
      activeTab = 'validation';
      if (validationResult.valid) {
        notifications.success('Validation passed!');
      } else {
        notifications.warning(`Validation found ${validationResult.errors.length} error(s)`);
      }
    } catch (error) {
      notifications.error(`Validation failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      validating = false;
    }
  }

  async function downloadValidationReport() {
    try {
      const blob = await apiService.downloadValidationReport(result.nexus_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${result.nexus_id}_validation_report.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      notifications.error(`Download failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  function isKeyChanged(key: string): boolean {
    return changedKeysSet.has(key);
  }

  function hasValidationError(nexusPath: string): boolean {
    return validationErrorPaths.has(nexusPath);
  }

  function getMappingPercentage(): number {
    const count = Object.keys(currentMapping).length;
    return result.total_metadata_keys > 0
      ? Math.round((count / result.total_metadata_keys) * 100)
      : 0;
  }

  $: mappedCount = Object.keys(currentMapping).length;
  $: mappingPercentage = getMappingPercentage();
  $: mappingStatusColor = mappingPercentage >= 80 ? 'green' : mappingPercentage >= 50 ? 'yellow' : 'red';
</script>

<div class="results-dashboard">
  <div class="dashboard-header">
    <h2 class="dashboard-title">NeXus Results</h2>
    <div class="dashboard-actions">
      <button
        on:click={downloadNexusFile}
        class="download-button"
        disabled={!result.nexus_id}
      >
        Download NeXus File
      </button>
      <button
        on:click={validateNexus}
        class="validate-button"
        disabled={!result.nexus_id || validating}
      >
        {#if validating}
          <span class="spinner"></span> Validating...
        {:else}
          Validate
        {/if}
      </button>
    </div>
  </div>

  <div class="dashboard-tabs">
    <button
      class="tab-button"
      class:active={activeTab === 'overview'}
      on:click={() => activeTab = 'overview'}
    >
      Overview
    </button>
    <button
      class="tab-button"
      class:active={activeTab === 'mapping'}
      on:click={() => activeTab = 'mapping'}
    >
      Mapping Details
    </button>
    <button
      class="tab-button"
      class:active={activeTab === 'validation'}
      on:click={() => activeTab = 'validation'}
      disabled={!validationResult}
    >
      Validation {#if validationResult}
        <span class="tab-badge" class:badge-ok={validationResult.valid} class:badge-error={!validationResult.valid}>
          {validationResult.valid ? '✓' : validationResult.errors.length}
        </span>
      {/if}
    </button>
    <button
      class="tab-button"
      class:active={activeTab === 'raw'}
      on:click={() => activeTab = 'raw'}
    >
      Raw Metadata
    </button>
  </div>

  <div class="dashboard-content">
    {#if activeTab === 'overview'}
      <div class="overview-panel">
        <div class="metrics-grid">
          <div class="metric-card">
            <div class="metric-label">Detected Instrument</div>
            <div class="metric-value">{result.detected_instrument}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Experiment Type</div>
            <div class="metric-value">{result.detected_experiment}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">NeXus Definition</div>
            <div class="metric-value">{result.nexus_definition}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Mapping Success</div>
            <div class="metric-value text-{mappingStatusColor}-600">
              {mappedCount}/{result.total_metadata_keys} ({mappingPercentage}%)
            </div>
            <div class="metric-bar">
              <div
                class="metric-bar-fill bg-{mappingStatusColor}-500"
                style="width: {mappingPercentage}%"
              ></div>
            </div>
          </div>
        </div>

        {#if result.suggestion}
          <div class="suggestion-card">
            <h3 class="suggestion-title">Recommendation</h3>
            <p class="suggestion-text">{result.suggestion}</p>
          </div>
        {/if}
      </div>
     {:else if activeTab === 'mapping'}
      <div class="mapping-panel">
        <h3 class="panel-title">Metadata Mapping</h3>
        <div class="mapping-header">
          <div class="mapping-header-item">Metadata Key</div>
          <div class="mapping-header-item">NeXus Path</div>
          <div class="mapping-header-item">Value</div>
        </div>
        <div class="mapping-items">
          <!-- Show removed keys first with strikethrough -->
          {#each removedKeys as { key, oldPath }}
            <div class="mapping-item removed">
              <div class="mapping-source strikethrough" title={key}>{key}</div>
              <div class="mapping-target strikethrough" title={oldPath}>{oldPath}</div>
              <div class="mapping-value strikethrough" title={String(originalMetadata[key] || '')}>{originalMetadata[key] || ''}</div>
              <div class="removed-badge">Removed</div>
            </div>
          {/each}
          <!-- Show current mappings -->
          {#each Object.entries(currentMapping) as [sourceKey, path]}
            <div class="mapping-item" class:changed={isKeyChanged(sourceKey)} class:has-error={hasValidationError(path)}>
              <div class="mapping-source" title={sourceKey}>{sourceKey}</div>
              <div class="mapping-target" title={path}>
                {#if hasValidationError(path)}<span class="error-icon" title="Validation error at this path">⚠</span>{/if}
                {path}
              </div>
              <div class="mapping-value" title={String(originalMetadata[sourceKey] || '')}>{originalMetadata[sourceKey] || ''}</div>
              {#if isKeyChanged(sourceKey)}
                <div class="changed-badge">Modified</div>
              {/if}
            </div>
          {/each}
        </div>
      </div>
    {:else if activeTab === 'validation'}
      <div class="validation-panel">
        {#if validationResult}
          <!-- Summary banner -->
          <div class="validation-summary" class:summary-ok={validationResult.valid} class:summary-error={!validationResult.valid}>
            <span class="validation-icon">{validationResult.valid ? '✓' : '✗'}</span>
            <div>
              <div class="validation-headline">
                {validationResult.valid ? 'Validation Passed' : 'Validation Failed'}
              </div>
              <div class="validation-detail">
                Definition: {validationResult.definition} — {validationResult.summary}
              </div>
            </div>
            <button class="download-report-button" on:click={downloadValidationReport}>
              Download Report
            </button>
          </div>

          <!-- Errors -->
          {#if validationResult.errors.length > 0}
            <div class="validation-section">
              <h4 class="validation-section-title error-title">
                Errors ({validationResult.errors.length})
              </h4>
              <div class="validation-items">
                {#each validationResult.errors as entry}
                  <div class="validation-item error-item">
                    <code class="validation-path">{entry.path || '(root)'}</code>
                    <span class="validation-message">{entry.message}</span>
                  </div>
                {/each}
              </div>
            </div>
          {/if}

          <!-- Warnings -->
          {#if validationResult.warnings.length > 0}
            <div class="validation-section">
              <h4 class="validation-section-title warning-title">
                Warnings ({validationResult.warnings.length})
              </h4>
              <div class="validation-items">
                {#each validationResult.warnings as entry}
                  <div class="validation-item warning-item">
                    <code class="validation-path">{entry.path || '(root)'}</code>
                    <span class="validation-message">{entry.message}</span>
                  </div>
                {/each}
              </div>
            </div>
          {/if}

          <!-- OK fields (collapsible) -->
          {#if validationResult.info.length > 0}
            <details class="validation-section">
              <summary class="validation-section-title ok-title">
                Passed ({validationResult.info.length})
              </summary>
              <div class="validation-items">
                {#each validationResult.info as entry}
                  <div class="validation-item ok-item">
                    <code class="validation-path">{entry.path}</code>
                    <span class="validation-message">{entry.message}</span>
                  </div>
                {/each}
              </div>
            </details>
          {/if}
        {:else}
          <p class="text-gray-500">Click "Validate" to check the NeXus file against the application definition.</p>
        {/if}
      </div>
    {:else if activeTab === 'raw'}
      <div class="raw-panel">
        <h3 class="panel-title">Original Metadata</h3>
        <div class="raw-content">
          <pre class="raw-json">{JSON.stringify(originalMetadata, null, 2)}</pre>
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .results-dashboard {
    @apply bg-white rounded-lg shadow border border-gray-200;
  }

  .dashboard-header {
    @apply p-6 border-b border-gray-200 flex items-center justify-between;
  }

  .dashboard-title {
    @apply text-2xl font-bold text-gray-900;
  }

  .dashboard-actions {
    @apply flex items-center space-x-3;
  }

  .download-button {
    @apply px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed;
  }

  .dashboard-tabs {
    @apply flex border-b border-gray-200;
  }

  .tab-button {
    @apply px-6 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset;
    border-bottom: 2px solid transparent;
  }

  .tab-button.active {
    @apply text-blue-600 bg-blue-50 border-blue-600;
  }

  .dashboard-content {
    @apply p-6;
  }

  .overview-panel {
    @apply space-y-6;
  }

  .metrics-grid {
    @apply grid grid-cols-1 md:grid-cols-2 gap-4;
  }

  .metric-card {
    @apply p-4 bg-gray-50 rounded-lg border border-gray-200;
  }

  .metric-label {
    @apply text-sm font-medium text-gray-600 mb-1;
  }

  .metric-value {
    @apply text-lg font-semibold text-gray-900 mb-2;
  }

  .metric-bar {
    @apply w-full bg-gray-200 rounded-full h-2;
  }

  .metric-bar-fill {
    @apply h-2 rounded-full transition-all duration-300;
  }

  .suggestion-card {
    @apply p-4 bg-blue-50 rounded-lg border border-blue-200;
  }

  .suggestion-title {
    @apply text-sm font-semibold text-blue-900 mb-2;
  }

  .suggestion-text {
    @apply text-sm text-blue-800;
  }

  .mapping-panel, .raw-panel {
    @apply space-y-4;
  }

  .panel-title {
    @apply text-lg font-semibold text-gray-900 mb-4;
  }

  .mapping-header {
    @apply grid grid-cols-3 gap-4 p-3 bg-gray-100 rounded-t-lg font-semibold text-sm text-gray-700;
  }

  .mapping-header-item {
    @apply text-center;
  }

  .mapping-items {
    @apply space-y-2 max-h-96 overflow-y-auto;
  }

  .mapping-item {
    @apply grid grid-cols-3 gap-4 items-center p-3 bg-gray-50 rounded-lg relative;
  }

  .mapping-item.changed {
    @apply bg-yellow-100 border-2 border-yellow-400 shadow-md;
    animation: highlight-pulse 2s ease-in-out;
  }

  @keyframes highlight-pulse {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(250, 204, 21, 0.4);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(250, 204, 21, 0);
    }
  }

  .changed-badge {
    @apply absolute -top-2 -right-2 px-2 py-0.5 text-xs font-bold text-yellow-900 bg-yellow-400 rounded-full;
  }

  .mapping-item.removed {
    @apply bg-red-100 border-2 border-red-400 shadow-md opacity-75;
    animation: fade-out 10s ease-in-out forwards;
  }

  @keyframes fade-out {
    0%, 80% {
      opacity: 0.75;
    }
    100% {
      opacity: 0.4;
    }
  }

  .strikethrough {
    @apply line-through text-red-600;
  }

  .removed-badge {
    @apply absolute -top-2 -right-2 px-2 py-0.5 text-xs font-bold text-red-900 bg-red-400 rounded-full;
  }

  .mapping-source {
    @apply font-mono text-sm text-gray-700 truncate;
  }

  .mapping-target {
    @apply font-mono text-sm text-blue-700 truncate;
  }

  .mapping-value {
    @apply font-mono text-sm text-green-700 truncate;
  }

  .raw-content {
    @apply max-h-96 overflow-auto bg-gray-50 rounded-lg;
  }

  .raw-json {
    @apply p-4 text-xs font-mono text-gray-700 whitespace-pre-wrap;
    margin: 0;
  }

  /* Validate button */
  .validate-button {
    @apply px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed;
  }

  .spinner {
    @apply inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Tab badge */
  .tab-badge {
    @apply inline-flex items-center justify-center ml-1 px-1.5 py-0.5 text-xs font-bold rounded-full;
  }
  .badge-ok {
    @apply bg-green-100 text-green-800;
  }
  .badge-error {
    @apply bg-red-100 text-red-800;
  }

  /* Validation panel */
  .validation-panel {
    @apply space-y-4;
  }

  .validation-summary {
    @apply flex items-center gap-4 p-4 rounded-lg;
  }
  .summary-ok {
    @apply bg-green-50 border border-green-300;
  }
  .summary-error {
    @apply bg-red-50 border border-red-300;
  }
  .validation-icon {
    @apply text-2xl font-bold;
  }
  .summary-ok .validation-icon {
    @apply text-green-600;
  }
  .summary-error .validation-icon {
    @apply text-red-600;
  }
  .validation-headline {
    @apply text-lg font-semibold text-gray-900;
  }
  .validation-detail {
    @apply text-sm text-gray-600;
  }

  .download-report-button {
    @apply ml-auto px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300;
  }

  .validation-section {
    @apply mt-3;
  }
  .validation-section-title {
    @apply text-sm font-semibold mb-2 cursor-pointer;
  }
  .error-title {
    @apply text-red-700;
  }
  .warning-title {
    @apply text-yellow-700;
  }
  .ok-title {
    @apply text-green-700;
  }

  .validation-items {
    @apply space-y-1 max-h-64 overflow-y-auto;
  }
  .validation-item {
    @apply flex items-start gap-3 px-3 py-2 rounded text-sm;
  }
  .error-item {
    @apply bg-red-50;
  }
  .warning-item {
    @apply bg-yellow-50;
  }
  .ok-item {
    @apply bg-green-50;
  }
  .validation-path {
    @apply text-xs font-mono text-gray-700 shrink-0 min-w-48;
  }
  .validation-message {
    @apply text-gray-800;
  }

  /* Mapping row error highlight */
  .mapping-item.has-error {
    @apply border-l-4 border-red-500;
  }
  .error-icon {
    @apply text-red-500 mr-1;
  }


</style>